import { useCallback, useState } from "react";
import { Navigate } from "react-router-dom";
import { Plus } from "lucide-react";

import {
  TenantsService,
  type TenantListResponse,
  type TenantProvisionPayload,
  type TenantProvisionResponse,
  type TenantSummary,
  type TenantTrialExtendPayload,
} from "@/api";
import { useApi, useMutation } from "@/hooks/use-api";
import { useAuth } from "@/hooks/use-auth";
import { useTranslation } from "@/i18n";
import { isSuperAdmin } from "@/lib/roles";
import { parseApiError } from "@/lib/api-error";
import { formatDateID } from "@/lib/format";
import { ROUTES } from "@/lib/constants";
import { Alert } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogBody,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { FormField } from "@/components/ui/form-field";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableEmpty,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { PageHeader } from "@/components/shared/page-header";
import { Section } from "@/components/shared/section";
import { TenantProvisionShowOnceDialog } from "@/components/platform/tenant-provision-show-once-dialog";

const TRIAL_DAYS_DEFAULT = 45;

/** Super-admin cross-tenant provisioning panel. Route-level guard +
 *  nav hide keep tenant admins from ever seeing this surface; the
 *  BE additionally rejects every endpoint hit with 403. */
export default function PlatformTenantsPage() {
  const { user } = useAuth();

  // Route guard — defence-in-depth alongside the BE 403. A tenant
  // admin manually typing the URL bounces back to the dashboard
  // instead of seeing a 403 splash.
  if (user === null) {
    // useAuth still loading.
    return null;
  }
  if (!isSuperAdmin(user.role)) {
    return <Navigate to={ROUTES.DASHBOARD} replace />;
  }

  return <PlatformTenantsPanel />;
}

function PlatformTenantsPanel() {
  const { t } = useTranslation();
  const fetcher = useCallback(() => TenantsService.listAll(), []);
  const list = useApi<TenantListResponse>(fetcher);

  const [createOpen, setCreateOpen] = useState(false);
  const [showOnce, setShowOnce] = useState<TenantProvisionResponse | null>(
    null
  );

  const [extendTarget, setExtendTarget] = useState<TenantSummary | null>(
    null
  );

  const provisionMutation = useMutation<
    TenantProvisionPayload,
    TenantProvisionResponse
  >((payload) => TenantsService.provision(payload), {
    onSuccess: (result) => {
      setCreateOpen(false);
      setShowOnce(result);
      void list.refetch();
    },
  });

  const extendMutation = useMutation<
    { tenantId: string; payload: TenantTrialExtendPayload },
    unknown
  >(({ tenantId, payload }) => TenantsService.extendTrial(tenantId, payload), {
    onSuccess: () => {
      setExtendTarget(null);
      void list.refetch();
    },
  });

  return (
    <div className="space-y-6">
      <PageHeader
        title={t("platform.tenants.page.title")}
        description={t("platform.tenants.page.description")}
        action={
          <Button onClick={() => setCreateOpen(true)}>
            <Plus size={14} />
            {t("platform.tenants.page.createButton")}
          </Button>
        }
      />

      <Section
        title={t("platform.tenants.list.title")}
        description={t("platform.tenants.list.description")}
      >
        {list.error ? (
          <Alert variant="warning">{parseApiError(list.error)}</Alert>
        ) : list.isLoading && !list.data ? (
          <Skeleton className="h-40 w-full" />
        ) : (
          <div className="overflow-hidden rounded-lg border border-ocean-100 bg-white">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{t("platform.tenants.list.colName")}</TableHead>
                  <TableHead>{t("platform.tenants.list.colSlug")}</TableHead>
                  <TableHead>
                    {t("platform.tenants.list.colTrialStatus")}
                  </TableHead>
                  <TableHead>
                    {t("platform.tenants.list.colCreated")}
                  </TableHead>
                  <TableHead aria-label={t("platform.tenants.list.colActions")} />
                </TableRow>
              </TableHeader>
              <TableBody>
                {!list.data || list.data.items.length === 0 ? (
                  <TableEmpty
                    colSpan={5}
                    message={t("platform.tenants.list.empty")}
                  />
                ) : (
                  list.data.items.map((tenant) => (
                    <TableRow key={tenant.id}>
                      <TableCell className="font-medium text-ocean-900">
                        {tenant.name}
                      </TableCell>
                      <TableCell>
                        <code className="text-xs">{tenant.slug}</code>
                      </TableCell>
                      <TableCell>
                        <TrialStatusBadge tenant={tenant} />
                      </TableCell>
                      <TableCell>{formatDateID(tenant.created_at)}</TableCell>
                      <TableCell className="text-right">
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          disabled={tenant.trial_status === "unlimited"}
                          onClick={() => setExtendTarget(tenant)}
                          title={
                            tenant.trial_status === "unlimited"
                              ? t(
                                  "platform.tenants.list.extendUnavailable"
                                )
                              : undefined
                          }
                        >
                          {t("platform.tenants.list.extendButton")}
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        )}
      </Section>

      <CreateTenantDialog
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onSubmit={(payload) => provisionMutation.mutate(payload)}
        isSubmitting={provisionMutation.isLoading}
        submitError={provisionMutation.error}
      />

      <TenantProvisionShowOnceDialog
        result={showOnce}
        onClose={() => setShowOnce(null)}
      />

      <ExtendTrialDialog
        tenant={extendTarget}
        onClose={() => setExtendTarget(null)}
        onSubmit={(payload) =>
          extendMutation.mutate({
            tenantId: extendTarget!.id,
            payload,
          })
        }
        isSubmitting={extendMutation.isLoading}
        submitError={extendMutation.error}
      />
    </div>
  );
}

function TrialStatusBadge({ tenant }: { tenant: TenantSummary }) {
  const { t } = useTranslation();
  if (tenant.trial_status === "unlimited") {
    return (
      <Badge tone="ocean">
        {t("platform.tenants.list.statusUnlimited")}
      </Badge>
    );
  }
  if (tenant.trial_status === "expired") {
    return (
      <Badge tone="danger">
        {t("platform.tenants.list.statusExpired", {
          days: Math.abs(tenant.trial_days_remaining ?? 0),
        })}
      </Badge>
    );
  }
  return (
    <Badge tone="success">
      {t("platform.tenants.list.statusTrial", {
        days: tenant.trial_days_remaining ?? 0,
      })}
    </Badge>
  );
}

function CreateTenantDialog({
  open,
  onClose,
  onSubmit,
  isSubmitting,
  submitError,
}: {
  open: boolean;
  onClose: () => void;
  onSubmit: (payload: TenantProvisionPayload) => void;
  isSubmitting: boolean;
  submitError: Error | null;
}) {
  const { t } = useTranslation();
  const [name, setName] = useState("");
  const [slug, setSlug] = useState("");
  const [slugDirty, setSlugDirty] = useState(false);
  const [regionCode, setRegionCode] = useState("id");
  const [adminEmail, setAdminEmail] = useState("");
  const [adminName, setAdminName] = useState("");
  const [trialDays, setTrialDays] = useState(String(TRIAL_DAYS_DEFAULT));

  const handleNameChange = (v: string) => {
    setName(v);
    if (!slugDirty) {
      setSlug(
        v
          .toLowerCase()
          .trim()
          .replace(/[^a-z0-9]+/g, "-")
          .replace(/(^-|-$)/g, "")
      );
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const days = Number(trialDays);
    onSubmit({
      name: name.trim(),
      slug: slug.trim(),
      region_code: regionCode.trim(),
      admin_email: adminEmail.trim(),
      admin_name: adminName.trim(),
      trial_days: Number.isFinite(days) ? days : TRIAL_DAYS_DEFAULT,
    });
  };

  const canSubmit =
    name.trim().length > 0 &&
    slug.trim().length > 0 &&
    regionCode.trim().length > 0 &&
    adminEmail.trim().length > 0 &&
    adminName.trim().length > 0 &&
    !isSubmitting;

  return (
    <Dialog open={open} onClose={onClose} size="max-w-xl">
      <DialogHeader>
        <DialogTitle>{t("platform.tenants.create.title")}</DialogTitle>
        <DialogDescription>
          {t("platform.tenants.create.description")}
        </DialogDescription>
      </DialogHeader>
      <form onSubmit={handleSubmit}>
        <DialogBody>
          {submitError && (
            <Alert variant="danger">{parseApiError(submitError)}</Alert>
          )}
          <div className="grid grid-cols-1 gap-4 tablet:grid-cols-2">
            <FormField
              label={t("platform.tenants.create.nameLabel")}
              value={name}
              onChange={handleNameChange}
              required
              autoFocus
            />
            <FormField
              label={t("platform.tenants.create.slugLabel")}
              value={slug}
              onChange={(v) => {
                setSlug(v);
                setSlugDirty(true);
              }}
              required
              hint={t("platform.tenants.create.slugHint")}
            />
          </div>
          <div className="grid grid-cols-1 gap-4 tablet:grid-cols-2">
            <FormField
              label={t("platform.tenants.create.regionLabel")}
              value={regionCode}
              onChange={setRegionCode}
              required
              hint={t("platform.tenants.create.regionHint")}
            />
            <FormField
              label={t("platform.tenants.create.trialDaysLabel")}
              type="number"
              value={trialDays}
              onChange={setTrialDays}
              min={1}
              max={365}
              required
              hint={t("platform.tenants.create.trialDaysHint")}
            />
          </div>
          <div className="grid grid-cols-1 gap-4 tablet:grid-cols-2">
            <FormField
              label={t("platform.tenants.create.adminEmailLabel")}
              type="email"
              value={adminEmail}
              onChange={setAdminEmail}
              required
            />
            <FormField
              label={t("platform.tenants.create.adminNameLabel")}
              value={adminName}
              onChange={setAdminName}
              required
            />
          </div>
          <Alert variant="info">
            {t("platform.tenants.create.passwordHint")}
          </Alert>
        </DialogBody>
        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={onClose}
            disabled={isSubmitting}
          >
            {t("platform.tenants.create.cancel")}
          </Button>
          <Button type="submit" disabled={!canSubmit}>
            {isSubmitting
              ? t("platform.tenants.create.submitLoading")
              : t("platform.tenants.create.submit")}
          </Button>
        </DialogFooter>
      </form>
    </Dialog>
  );
}

function ExtendTrialDialog({
  tenant,
  onClose,
  onSubmit,
  isSubmitting,
  submitError,
}: {
  tenant: TenantSummary | null;
  onClose: () => void;
  onSubmit: (payload: TenantTrialExtendPayload) => void;
  isSubmitting: boolean;
  submitError: Error | null;
}) {
  const { t } = useTranslation();
  const [extraDays, setExtraDays] = useState("30");
  const [reason, setReason] = useState("");

  if (!tenant) return null;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const days = Number(extraDays);
    if (!Number.isFinite(days) || days < 1 || days > 365) return;
    onSubmit({
      extra_days: days,
      reason: reason.trim() || undefined,
    });
  };

  return (
    <Dialog open onClose={onClose} size="max-w-md">
      <DialogHeader>
        <DialogTitle>{t("platform.tenants.extend.title")}</DialogTitle>
        <DialogDescription>
          {t("platform.tenants.extend.description", {
            tenant: tenant.name,
          })}
        </DialogDescription>
      </DialogHeader>
      <form onSubmit={handleSubmit}>
        <DialogBody>
          {submitError && (
            <Alert variant="danger">{parseApiError(submitError)}</Alert>
          )}
          <FormField
            label={t("platform.tenants.extend.daysLabel")}
            type="number"
            value={extraDays}
            onChange={setExtraDays}
            min={1}
            max={365}
            required
            autoFocus
          />
          <FormField
            label={t("platform.tenants.extend.reasonLabel")}
            value={reason}
            onChange={setReason}
            maxLength={500}
            hint={t("platform.tenants.extend.reasonHint")}
          />
        </DialogBody>
        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={onClose}
            disabled={isSubmitting}
          >
            {t("platform.tenants.extend.cancel")}
          </Button>
          <Button type="submit" disabled={isSubmitting}>
            {isSubmitting
              ? t("platform.tenants.extend.submitLoading")
              : t("platform.tenants.extend.submit")}
          </Button>
        </DialogFooter>
      </form>
    </Dialog>
  );
}
